import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import AdminLayout from '@/components/AdminLayout';
import { ArrowLeft, FileText, Send } from 'lucide-react';

const AdminGenerateInvoice = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    description: '',
    due_date: ''
  });

  useEffect(() => {
    loadData();
  }, [memberId]);

  const loadData = async () => {
    try {
      const [memberRes, settingsRes] = await Promise.all([
        apiClient.get(`/admin/members/${memberId}`),
        apiClient.get('/settings/')
      ]);
      setMember(memberRes.data);
      setSettings(settingsRes.data);
      
      // Pre-fill with monthly fee from settings
      if (settingsRes.data?.membership_fee) {
        const fee = settingsRes.data.membership_fee;
        setForm(prev => ({
          ...prev,
          amount: fee.monthly_fee.toString(),
          description: `Monthly membership fee - ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
        }));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.amount || !form.description || !form.due_date) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/invoices/generate', null, {
        params: {
          member_id: memberId,
          amount: parseFloat(form.amount),
          description: form.description,
          due_date: form.due_date
        }
      });
      toast.success('Invoice generated successfully!');
      navigate(`/admin/members/${memberId}/payments`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="members" title="Generate Invoice">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  const currency = settings?.membership_fee?.currency || 'USD';

  return (
    <AdminLayout currentPage="members" title="Generate Invoice">
      <div className="max-w-2xl">
        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/members/${memberId}/payments`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Payments
          </Button>
        </div>

        {member && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">Member Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{member.title} {member.first_name} {member.last_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{member.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Member ID</p>
                  <p className="font-medium">{member.member_id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Membership Type</p>
                  <p className="font-medium">{member.membership_type}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {settings?.membership_fee && (
          <Card className="mb-6 bg-muted/50">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">Membership Fee Settings</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Monthly Fee</p>
                  <p className="font-medium text-lg">{currency} {settings.membership_fee.monthly_fee}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Membership Period</p>
                  <p className="font-medium">{settings.membership_fee.membership_period_months} months</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              New Invoice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="amount">Amount ({currency})</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  data-testid="invoice-amount"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="e.g., Monthly membership fee - January 2025"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                  data-testid="invoice-description"
                />
              </div>
              
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  required
                  data-testid="invoice-due-date"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={submitting} data-testid="generate-invoice-btn">
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Generating...' : 'Generate Invoice'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(`/admin/members/${memberId}/payments`)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminGenerateInvoice;
